import { FC, useMemo } from "react";
import {
  MeasurementSystemEnum,
  Nullable,
  SnowReportId,
  useAdminWeatherReportControllerDeleteWeatherReport,
} from "service";
import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../components/generic-table/generic-table";
import InteractableCell from "../components/generic-table/interactable-cell";
import { useDialogStore } from "../../../state/dialog-store";
import { weatherReportSeverityTitleMap } from "service/types/enum/weather-severity-enum";
import {
  formatDateTime,
  formattedValueWithSymbol,
} from "../../../utils/string-helpers";
import { AdminWeatherReportResDto } from "service/api/estabrookSchemas";
import { useNavigate } from "react-router-dom";
import { generateRoute } from "ui";
import { AdRoutes } from "../../../constants/ad-routes";
import { toast } from "react-toastify";
import { queryClient } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";

const sanitizedHeader = [
  {
    label: "Date Modified",
    value: "updatedAt",
    sortable: true,
  },
  {
    label: "Address",
    value: "addressShort",
    sortable: true,
    sortableValue: "address",
  },
  {
    label: "Reporter",
    value: "reporter",
    sortable: true,
  },
  {
    label: "Weather Conditions",
    value: "severity",
  },
  {
    label: "Snow Level",
    value: "snowAmount",
    sortable: true,
  },
  {
    label: "Elevation",
    value: "elevation",
    sortable: true,
  },
];

export interface CustomerManagementTableProps {
  data?: AdminWeatherReportResDto[];
  isLoading?: boolean;
  sortOptions: Nullable<SortOptions>;
  sortOptionsChange: (value: Nullable<SortOptions>) => void;
}

const SnowReportsTable: FC<CustomerManagementTableProps> = (props) => {
  const { data = [], isLoading, sortOptions, sortOptionsChange } = props;
  const { setDialog } = useDialogStore();
  const navigate = useNavigate();
  const { mutate } = useAdminWeatherReportControllerDeleteWeatherReport({
    onSuccess: () => {
      toast.success("Report Deleted Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/weather-reports",
          operationId: "adminWeatherReportControllerGetAll",
          variables: {},
        }),
      );
    },
  });
  const onDeletePress = async (snowReportId: SnowReportId) => {
    setDialog({
      title: "Delete Snow Report",
      description:
        "Are you sure you want to delete this Snow Report? <br />This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        onClose();
        mutate({ pathParams: { reportId: snowReportId } });
      },
    });
  };

  const onReportDetail = (id: SnowReportId) => {
    navigate(generateRoute(AdRoutes.snowReportsDetail, { reportId: id }));
  };

  const onRowClick = (row: AdminWeatherReportResDto, e: React.MouseEvent) => {
    // Ignore clicks on the in-cell edit/delete buttons.
    if ((e.target as HTMLElement).closest("button")) {
      return;
    }
    onReportDetail(row.id);
  };

  const renderRowLabel: Record<
    string,
    RenderGenericRowFunction<AdminWeatherReportResDto>
  > = useMemo(
    () => ({
      updatedAt: ({ headerValue, data: { updatedAt } }) => ({
        label: formatDateTime(updatedAt),
        value: headerValue,
      }),
      elevation: ({
        headerValue,
        data: { elevation, elevationMin, elevationMax, id },
      }) => ({
        label: (
          <InteractableCell
            label={
              elevationMin != null && elevationMax != null
                ? `${elevationMin} - ${elevationMax} ft`
                : elevation
                  ? `${elevation.imperial.value} ft`
                  : "-"
            }
            onDelete={() => onDeletePress(id)}
            onEdit={() => onReportDetail(id)}
          />
        ),
        value: headerValue,
      }),
      snowAmount: ({ headerValue, data: { snowAmount } }) => ({
        // Show inches as a double prime (″) rather than the backend's " symbol.
        label: snowAmount
          ? formattedValueWithSymbol(
              snowAmount.imperial,
              MeasurementSystemEnum.imperial,
            ).replace(/"/g, "″")
          : "-",
        value: headerValue,
      }),
      severity: ({ headerValue, data: { severity } }) => ({
        label: weatherReportSeverityTitleMap[severity],
        value: headerValue,
      }),
      addressShort: ({ headerValue, data: { address } }) => ({
        label: address ?? "-",
        value: headerValue,
      }),
      reporter: ({ headerValue, data: { reporter } }) => ({
        label: reporter ?? "-",
        value: headerValue,
      }),
    }),
    [],
  );

  return (
    <GenericTable
      sx={{ mt: "20px" }}
      headers={sanitizedHeader}
      renderRowLabel={renderRowLabel}
      data={data}
      isLoading={isLoading}
      onRowClick={onRowClick}
      sortOptions={sortOptions}
      onSortChange={sortOptionsChange}
    />
  );
};

export default SnowReportsTable;
